// chat-builder/imageAssetApi.js
// V3 이미지 자산(에셋) API 얇은 래퍼. 서버 Chat.js 엔드포인트를 Promise로 감싼다.
//
// 자산 모델: { id(assetId=ref, 불변), name, folderPath(가상 폴더), url(current 버전), versionNo, versionCount }
// 업로드/교체는 prepare(id·키·presigned 발급) → S3 PUT → commit(DB 확정) 3단계.
import { PRConnect } from "@projectroom/ui-lib";
import axios from "axios";

const send = (endpoint, param) =>
	new Promise((resolve, reject) => {
		PRConnect.send(endpoint, param, (data) => {
			if (data?.header?.status === "success") resolve(data.result);
			else reject(new Error(data?.header?.message || "요청에 실패했습니다."));
		});
	});

/**
 * 자산 목록.
 *
 * ⚠️ `kind` 는 **라이브러리를 가르는 축**이다 — 'image'(기본)면 그림만, 'audio' 면 음원만 온다.
 *    섞어 받으면 그림 피커에 깨진 썸네일이 뜨고(음원은 `<img>` 로 못 그린다) 소리 피커에는
 *    재생 안 되는 칸이 생긴다. 서버가 KIND 컬럼으로 거른다(SERVER Chat.js assetKindOf).
 */
export const listImageAssets = (chatId, kind = "image") =>
	send("/api/Chat/getImageAssetList", { chatId, kind });

/** 소리(음원) 목록. 위와 같은 API 의 다른 칸이다. */
export const listSoundAssets = (chatId) => listImageAssets(chatId, "audio");
export const listAssetVersions = (assetId) => send("/api/Chat/getImageAssetVersions", { assetId });
export const rollbackAsset = (assetId, versionId) => send("/api/Chat/rollbackImageAsset", { assetId, versionId });
export const moveAsset = (chatId, assetId, folderPath) => send("/api/Chat/moveImageAsset", { chatId, assetId, folderPath });
export const moveFolder = (chatId, oldPrefix, newPrefix) => send("/api/Chat/moveImageFolder", { chatId, oldPrefix, newPrefix });
export const renameAsset = (chatId, assetId, name) => send("/api/Chat/renameImageAsset", { chatId, assetId, name });

/**
 * AI 참조로 쓸 수 있는 이미지인가(= 래스터인가).
 *
 * ⚠️ 라이브러리에는 **SVG 도 들어온다.** 그걸 참조로 보내면 OpenAI 가 `invalid_image_file` 만
 *    돌려줘서 저작자는 원인을 알 수 없다(실측). 서버도 바이트로 막지만, 세트 일괄 생성처럼
 *    11장이 걸린 자리에서는 **고르는 순간** 막아주는 편이 낫다.
 * ⚠️ 확장자 판정이라 완벽하지 않다 — 최종 판정은 서버(sniffRasterImage)가 한다.
 */
export const isRasterAsset = (url) => !/\.svgz?(\?|$)/i.test(String(url || ""));

export const deleteAsset = (chatId, assetId) => send("/api/Chat/deleteImageAsset", { chatId, assetId });

// ── AI 생성 ──
// 생성(S3 저장 + 후보 기록) → 커밋(자산으로 등록)의 2단계. 라이브러리에서 만들 때는 두 단계를
// 이어 붙여 "만들면 바로 내 폴더에 들어온다"로 쓴다(aiCreateAsset).
//
// ⚠️ referenceImage 는 **S3 키 또는 전체 URL** 둘 다 된다(서버 gptImageGenerate 주석 참고).
//    라이브러리 자산을 참조로 쓸 때는 그 자산의 url 을 그대로 넘기면 된다.
// ⚠️ name = **영숫자 슬러그**(파일명용). 안 주면 한글 프롬프트가 sanitize 에서 통째로 날아가
//    이름이 `_<timestamp>.png` 가 된다(서버 gptImageGenerate 주석 참고).
export const aiGenerate = (chatId, { prompt, imageType, targetPath, keywordList, referenceImage, name }) =>
	send("/api/Chat/gptImageGenerate", { chatId, prompt, imageType, targetPath, keywordList, referenceImage, name });

export const aiCommit = (chatId, imageType, fileName, folderPath = "") =>
	send("/api/Chat/commitAiImageAsset", { chatId, imageType, fileName, folderPath });

/** 생성 → 곧바로 자산 등록. 돌려주는 건 등록된 자산({assetId, url, name}). */
export async function aiCreateAsset(chatId, opts) {
	const made = await aiGenerate(chatId, opts);
	return await aiCommit(chatId, opts.imageType, made.fileName, opts.folderPath || "");
}

// 문맥 폴더(가상 폴더 "시작점") 유도 — 업로드 랜딩·이름변경 이동·마이그레이션이 "같은 규칙"을 쓰도록 한 곳에서 정의.
// 슬래시는 하위폴더로 오해되므로 공백 치환 + 앞뒤 공백 제거. 제목이 비면 id로 폴백.
// (⚠️ 서버 마이그레이션 SQL도 이 규칙 REPLACE(TITLE,'/',' ')+TRIM 과 맞춰야 함)
const sanitizeFolderSeg = (s) => (s ?? "").toString().replace(/\//g, " ").trim();
// 상위(문맥) 폴더명 = 타입 기반 영문 + 그 아래 봇/챗 제목으로 분리. (제목 비면 id 폴백)
//   봇: 엔터프라이즈=profilePage, 허브=hubPage, 2D/3D 캐릭터=characterPage.  챗=chat.
//   ⚠️ 봇은 pageType 으로 분기하므로, 이름변경 시 "옛 폴더" 계산 객체에도 pageType 을 반드시 넘길 것(오분류 방지).
//   ⚠️ HUB 를 빠뜨리면 else 로 떨어져 **허브 자산이 2D 캐릭터 폴더(characterPage/)에 섞인다.**
//      한동안 그 상태였고, migrations/learner-hub/IMAGE_ASSET_hub_folders.sql 이 옛 자산을 옮긴다.
const BOT_FOLDER_PREFIX = { ENTERPRISE: "profilePage", HUB: "hubPage", GAME: "gamePage" };
export const botAssetFolder = (bot) =>
	`${BOT_FOLDER_PREFIX[bot?.pageType] || "characterPage"}/${sanitizeFolderSeg(bot?.title) || bot?.id || ""}`;
export const chatAssetFolder = (chat) => `chat/${sanitizeFolderSeg(chat?.title) || chat?.chatId || ""}`;

/**
 * 올릴 수 있는 음원 형식.
 *
 * ⚠️ mp3 를 권한다 — **브라우저가 전부 디코드할 수 있는 유일한 형식**에 가깝다.
 *    ogg 는 사파리가, m4a/aac 는 일부 안드로이드 브라우저가 가려 받는다. Phaser 는
 *    디코드 실패를 "그 음원만 없음"으로 조용히 넘기므로, 저작자에게는 **어떤 기기에서만
 *    소리가 안 나는** 것으로 보인다(원인을 찾을 수 없는 종류의 고장).
 */
export const AUDIO_ACCEPT = ".mp3,.m4a,.aac,.ogg,.wav,audio/*";

/** 소리 자산인가(확장자 기준 — 피커에 잘못 들어온 파일을 고르는 순간 막는 용도). */
export const isAudioFile = (nameOrUrl) =>
	/\.(mp3|m4a|aac|ogg|oga|wav)(\?|$)/i.test(String(nameOrUrl || ""));

// prepare → S3 PUT → commit. assetId를 주면 기존 자산의 새 버전(교체), 없으면 새 자산.
// ⚠️ `kind` 는 **새 자산일 때만** 뜻이 있다(교체는 기존 자산의 종류를 그대로 쓴다) —
//    그래서 commitImageAssetVersion 에는 안 넘긴다. mp3 를 png 로 교체하는 길을 열지 않는 셈이다.
async function uploadOne(chatId, file, { folderPath = "", assetId = null, kind = "image" } = {}) {
	const prep = await send("/api/Chat/prepareImageAssetUpload", {
		chatId,
		name: file.name,
		type: file.type,
		assetId,
	});
	await axios.put(prep.presignedUrl, file, { headers: { "Content-Type": file.type } });
	if (prep.isNew) {
		return send("/api/Chat/commitImageAsset", {
			chatId,
			assetId: prep.assetId,
			versionId: prep.versionId,
			name: file.name,
			folderPath,
			s3Key: prep.s3Key,
			url: prep.url,
			kind,
		});
	}
	return send("/api/Chat/commitImageAssetVersion", {
		assetId: prep.assetId,
		versionId: prep.versionId,
		s3Key: prep.s3Key,
		url: prep.url,
	});
}

/** 새 자산 업로드 → 서버 자산 객체 반환({id, url, name, folderPath, ...}) */
export const createAsset = (chatId, file, folderPath = "") => uploadOne(chatId, file, { folderPath });
/** 새 음원 업로드. 그림과 **같은 라이브러리**의 다른 칸(KIND='audio')에 들어간다. */
export const createSoundAsset = (chatId, file, folderPath = "") =>
	uploadOne(chatId, file, { folderPath, kind: "audio" });
/** 기존 자산 교체(새 버전) → {assetId, versionId, versionNo, url} */
export const replaceAsset = (chatId, assetId, file) => uploadOne(chatId, file, { assetId });
